<script setup lang="ts">
import {usePatientStore} from "@/stores/patient.store";
import {computed, ref} from "vue";
import ConsumerService from "@/consumer/services/ConsumerService";
import {Pencil} from "@lucide/vue";
import ConsumerInformationService from "@/consumer/services/ConsumerInformationService";
import {CustomerDeclaredInformation} from "@/customer/interfaces/DeclaredInformation";
import {VidalAllergy, VidalPathology} from "@/customer/interfaces/Patient";
import ProfileModal from "@/interview/components/instance-modal/profile/ProfileModal.vue";
import {useToastStore} from "@/stores/toast.store";
import Icon from "@/core/components/icon/Icon.vue";
import usePatientProfile from "@/drug-interaction/composables/usePatientProfile";

export interface CustomerProfile extends CustomerDeclaredInformation {
    pathologies: VidalPathology[];
    allergies: VidalAllergy[];
}

const props = defineProps<{
    consumerKey: string;
    customerId: number;
}>();

const {getConsumer, patchCustomerProfile} = usePatientStore();
const modalOpen = ref(false);
const {showError, showSuccess} = useToastStore();
/**
 * The customer computed property.
 */
const customer = computed(() => {
    const consumer = getConsumer(props.consumerKey);

    if (!consumer || !ConsumerService.isCustomer(consumer)) {
        return null;
    }

    return consumer;
});

const {profile, saving, save} = usePatientProfile(customer, patchCustomerProfile);

/**
 * Save the customer profile.
 */
const saveProfile = async (data: Omit<CustomerProfile, "id" | "is_dead">) => {
    return save(data)
        .then(() => {
            modalOpen.value = false;
            showSuccess({
                content: "Le profil a été mis à jour avec succès.",
            });
        })
        .catch(() => {
            showError({
                content: "Une erreur est survenue lors de la mise à jour du profil."
            });
        });
};
</script>

<template>
  <div v-if="customer" class="p-5 space-y-4">
    <div v-if="customer.information" class="flex gap-2 items-baseline">
      <div class="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-sm">
        <Icon name="mdi-account" :size="20" color="white" />
      </div>
      <div>
        <span class="text-lg font-semibold block text-primary">
          {{ ConsumerInformationService.informationSummary(customer.information) }}
        </span>
      </div>
      <div>
        <ProfileModal
          v-model:open="modalOpen"
          :profile="profile"
          :name="customer.information.name"
          :loading="saving"
          @finish="saveProfile"
        >
          <button>
            <Pencil class="w-5 h-5" :size="16" />
          </button>
        </ProfileModal>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
